import type React from "react"
import { useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useAppSelector, useAppDispatch } from "../app/hooks"
import { changePassword, resetPassword } from "../features/auth/authAPI"
import { fetchAndRestoreUserData } from "../features/auth/authSlice"
import { toast } from "sonner"
import bgImage from "../assets/images/bg-img2.png";

export const SetNewPassword = () => {
    const navigate = useNavigate()
    const { token: routeToken } = useParams<{ token?: string }>()
    const [searchParams] = useSearchParams()
    const dispatch = useAppDispatch()
    const accessToken = useAppSelector((state) => state.auth.accessToken)
    const resetToken = routeToken || searchParams.get("token") || searchParams.get("reset_token") || ""
    const [password, setPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [passwordError, setPasswordError] = useState(false)
    const [confirmPasswordError, setConfirmPasswordError] = useState(false)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError(null)
        setPasswordError(false)
        setConfirmPasswordError(false)

        // Validation
        const passwordChangeToken = accessToken || resetToken
        if (!passwordChangeToken) {
            setError("Authentication required. Please login again.")
            return
        }

        if (password.length < 8) {
            setPasswordError(true)
            setError("Password must be at least 8 characters")
            return
        }

        if (password !== confirmPassword) {
            setConfirmPasswordError(true)
            setError("Passwords do not match")
            return
        }

        try {
            setIsSubmitting(true)
            if (accessToken) {
                await changePassword({
                    new_password: password,
                    confirm_password: confirmPassword,
                }, accessToken)

                // After successful password change, refetch user data to update pwd_reset_required flag
                try {
                    await dispatch(fetchAndRestoreUserData({ forceRefresh: true })).unwrap()
                } catch (error) {
                    console.error("Failed to refresh user data:", error)
                }
            } else {
                await resetPassword({
                    token: passwordChangeToken,
                    password,
                    confirm_password: confirmPassword,
                })
            }

            setIsSuccess(true)
            toast.success("Password changed successfully!")
        } catch (error) {
            const message = error instanceof Error ? error.message : "Failed to change password. Please try again."
            setError(message)
            toast.error(message)
        } finally {
            setIsSubmitting(false)
        }
    }

    if (isSuccess) {
        return (
            <div className="min-h-screen flex">
                {/* Left side - Success message */}
                <div className="w-full lg:w-1/2 bg-white flex items-center justify-center p-8">
                    <div className="max-w-md w-full space-y-6">
                        <h1 className="text-4xl font-bold text-gray-900 leading-tight">
                            Your password has been successfully reset!
                        </h1>
                        <p className="text-gray-600 text-base">
                            Your password has been successfully reset. You can now login with your new password.
                        </p>
                        <button
                            onClick={() => navigate(accessToken ? "/dashboard" : "/login", { state: { fromResetPassword: true } })}
                            className="btn-cta w-full font-bold py-4 px-6 rounded-md">
                            Done
                        </button>
                    </div>
                </div>

                {/* Right side - Black background with logo */}
                <div
                    className="hidden lg:flex w-1/2 h-screen bg-cover bg-center items-center justify-center p-4"
                    style={{ backgroundImage: `url(${bgImage})` }}
                >
                    <img
                        src="/assets/images/Logo-white-outline.png"
                        alt="Join Us"
                        className="w-40 h-auto object-contain"
                    />
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen flex">
            {/* Left side - Form */}
            <div className="w-full lg:w-1/2 bg-white flex items-center justify-center p-8">
                <div className="max-w-md w-full">
                    {/* Back button */}
                    <button
                        onClick={() => { navigate('/login') }}
                        className="flex items-center text-gray-700 mb-8 hover:text-gray-900 transition-colors">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Back
                    </button>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <h1 className="text-4xl font-bold text-gray-900 mb-3">Set new password</h1>
                            <p className="text-gray-600">Must be at least 8 characters.</p>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md">
                                {error}
                            </div>
                        )}

                        {/* Password field */}
                        <div className="space-y-2">
                            <label htmlFor="password" className="block text-sm text-gray-600">
                                Password
                            </label>
                            <div className="relative">
                                <input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value)
                                        setPasswordError(false)
                                        setError(null)
                                    }}
                                    className={"w-full bg-[#F7FAFC] px-4 py-3 rounded-md focus:outline-none placeholder-gray-400 transition-colors border " + (passwordError ? "border border-red-400 focus:ring-2 focus:ring-red-400 focus:border-red-400" : "border border-gray-200 focus:ring-2 focus:ring-lime-400 focus:border-transparent")}
                                    placeholder="••••••"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 px-1 h-2/3 top-1/2 border-l-2 border-[#CFD9E0] -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showPassword ? (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                            />
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                            />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                                            />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Confirm Password field */}
                        <div className="space-y-2">
                            <label htmlFor="confirm-password" className="block text-sm text-gray-600">
                                Password
                            </label>
                            <div className="relative">
                                <input
                                    id="confirm-password"
                                    type={showConfirmPassword ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={(e) => {
                                        setConfirmPassword(e.target.value)
                                        setConfirmPasswordError(false)
                                        setError(null)
                                    }}
                                    className={"w-full bg-[#F7FAFC] px-4 py-3 rounded-md focus:outline-none placeholder-gray-400 transition-colors border " + (confirmPasswordError ? "border border-red-400 focus:ring-2 focus:ring-red-400 focus:border-red-400" : "border border-gray-200 focus:ring-2 focus:ring-lime-400 focus:border-transparent")}
                                    placeholder="••••••"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 px-1 h-2/3 top-1/2 border-l-2 border-[#CFD9E0] -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showConfirmPassword ? (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                            />
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                            />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                                            />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Submit button */}
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="btn-cta w-full font-bold py-4 px-6 rounded-md disabled:opacity-60"
                        >
                            {isSubmitting ? "Submitting..." : "Submit"}
                        </button>
                    </form>
                </div>
            </div>

            {/* Right side - Black background with logo */}
            <div
                className="hidden lg:flex w-1/2 h-screen bg-cover bg-center items-center justify-center p-4"
                style={{ backgroundImage: `url(${bgImage})` }}
            >
                <img
                    src="/assets/images/Logo-white-outline.png"
                    alt="Join Us"
                    className="w-40 h-auto object-contain"
                />
            </div>
        </div>
    )
}
